import { useEffect, useState } from "react";
import { api, formatINR } from "@/lib/api";
import { SectionHeader } from "@/components/SectionHeader";
import { StatCard } from "@/components/StatCard";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { ArrowDownRight, ArrowUpRight, Plus } from "lucide-react";
import { Link } from "react-router-dom";

const PALETTE = ["#5C8065", "#D26A59", "#E8B253", "#829399", "#C5A880", "#1F4E3D", "#A78A5C", "#7A5E48"];

const monthLabel = (ym) => {
  if (!ym) return "";
  const [y, m] = ym.split("-");
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleString("en-IN", { month: "short", year: "2-digit" });
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="bg-white border border-[#EAE8E3] rounded-md px-3 py-2 shadow-md">
      <div className="label-overline mb-1">{monthLabel(label)}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="text-xs flex items-center gap-2 font-mono-num">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
          <span className="capitalize text-[#696E69]">{p.dataKey}:</span>
          <span className="text-[#212421]">₹{formatINR(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

export default function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [recent, setRecent] = useState([]);

  const load = async () => {
    try {
      const [s, t] = await Promise.all([
        api.get("/insights/summary"),
        api.get("/transactions"),
      ]);
      setSummary(s.data);
      setRecent(t.data.slice(0, 8));
    } catch (e) {
      console.warn("Dashboard load failed", e?.response?.status);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const trend = summary?.monthly_trend || [];
  const breakdown = (summary?.expense_breakdown || []).slice(0, 6);

  return (
    <div data-testid="dashboard-page">
      <SectionHeader
        overline="Overview · Today"
        title="Your money, grounded."
        description="A quiet view of your income, expenses, and where things flow this month."
        action={
          <Link
            to="/transactions"
            data-testid="dashboard-add-transaction"
            className="inline-flex items-center gap-2 bg-[#1F4E3D] text-white rounded-lg px-5 py-2.5 font-medium hover:bg-[#163B2E] transition-colors"
          >
            <Plus className="w-4 h-4" strokeWidth={1.5} /> New entry
          </Link>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          testId="stat-balance"
          label="Net Balance"
          value={summary?.balance ?? 0}
          accent={summary && summary.balance < 0 ? "debit" : "credit"}
          hint={summary ? `${summary.transaction_count} entries` : "—"}
        />
        <StatCard
          testId="stat-income"
          label="Total Income"
          value={summary?.total_income ?? 0}
          accent="credit"
          hint="All time"
        />
        <StatCard
          testId="stat-expense"
          label="Total Expense"
          value={summary?.total_expense ?? 0}
          accent="debit"
          hint="All time"
        />
        <StatCard
          testId="stat-month"
          label="This Month Spend"
          value={summary?.current_month_expense ?? 0}
          accent="warning"
          hint={`Earned ₹${formatINR(summary?.current_month_income ?? 0)}`}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-2 bg-white border border-[#EAE8E3] rounded-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="label-overline mb-1">Trend</div>
              <h3 className="font-display text-xl tracking-tight">Income vs Expense</h3>
            </div>
            <div className="flex items-center gap-4 text-xs text-[#696E69]">
              <span className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-[#5C8065]" /> Income
              </span>
              <span className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-[#D26A59]" /> Expense
              </span>
            </div>
          </div>
          <div className="h-72" data-testid="trend-chart">
            {trend.length === 0 ? (
              <div className="h-full flex items-center justify-center text-sm text-[#696E69]">
                Add a transaction to see your trend.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trend} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="incomeFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#5C8065" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#5C8065" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="expenseFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#D26A59" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#D26A59" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EAE8E3" vertical={false} />
                  <XAxis
                    dataKey="month"
                    tickFormatter={monthLabel}
                    stroke="#696E69"
                    fontSize={11}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    stroke="#696E69"
                    fontSize={11}
                    tickFormatter={(v) => `₹${v >= 1000 ? (v / 1000).toFixed(0) + "k" : v}`}
                    tickLine={false}
                    axisLine={false}
                    width={60}
                  />
                  <Tooltip content={<ChartTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="income"
                    stroke="#5C8065"
                    strokeWidth={2}
                    fill="url(#incomeFill)"
                  />
                  <Area
                    type="monotone"
                    dataKey="expense"
                    stroke="#D26A59"
                    strokeWidth={2}
                    fill="url(#expenseFill)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="bg-white border border-[#EAE8E3] rounded-lg p-6">
          <div className="label-overline mb-1">Breakdown</div>
          <h3 className="font-display text-xl tracking-tight mb-6">Top Expense Categories</h3>
          <div className="h-56" data-testid="category-chart">
            {breakdown.length === 0 ? (
              <div className="h-full flex items-center justify-center text-sm text-[#696E69]">
                No expenses yet.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={breakdown}
                    dataKey="amount"
                    nameKey="category"
                    innerRadius={50}
                    outerRadius={85}
                    paddingAngle={0}
                    stroke="none"
                  >
                    {breakdown.map((_, i) => (
                      <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(v) => `₹${formatINR(v)}`}
                    contentStyle={{
                      background: "#fff",
                      border: "1px solid #EAE8E3",
                      borderRadius: 6,
                      fontSize: 12,
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
          <ul className="mt-4 space-y-2">
            {breakdown.map((b, i) => (
              <li key={b.category} className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-2">
                  <span
                    className="w-2.5 h-2.5 rounded-full"
                    style={{ background: PALETTE[i % PALETTE.length] }}
                  />
                  <span className="text-[#212421]">{b.category}</span>
                </span>
                <span className="font-mono-num text-[#696E69]">₹{formatINR(b.amount)}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="bg-white border border-[#EAE8E3] rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="label-overline mb-1">Recent</div>
            <h3 className="font-display text-xl tracking-tight">Latest activity</h3>
          </div>
          <Link
            to="/transactions"
            data-testid="dashboard-view-all"
            className="text-sm text-[#1F4E3D] hover:underline"
          >
            View all →
          </Link>
        </div>
        {recent.length === 0 ? (
          <div className="text-sm text-[#696E69] py-12 text-center">No activity yet — add your first transaction.</div>
        ) : (
          <ul className="divide-y divide-[#EAE8E3]" data-testid="recent-list">
            {recent.map((t) => (
              <li key={t.id} className="flex items-center justify-between py-3">
                <div className="flex items-center gap-3">
                  <div
                    className={`w-9 h-9 rounded-md flex items-center justify-center ${
                      t.type === "income" ? "bg-[#5C8065]/12" : "bg-[#D26A59]/12"
                    }`}
                  >
                    {t.type === "income" ? (
                      <ArrowDownRight className="w-4 h-4 text-[#5C8065]" strokeWidth={1.75} />
                    ) : (
                      <ArrowUpRight className="w-4 h-4 text-[#D26A59]" strokeWidth={1.75} />
                    )}
                  </div>
                  <div>
                    <div className="text-sm text-[#212421]">{t.category}</div>
                    <div className="text-xs text-[#696E69]">
                      {t.date} {t.note ? `· ${t.note}` : ""}
                    </div>
                  </div>
                </div>
                <div
                  className={`font-mono-num text-sm ${
                    t.type === "income" ? "text-[#1F4E3D]" : "text-[#D26A59]"
                  }`}
                >
                  {t.type === "income" ? "+" : "−"} ₹{formatINR(t.amount)}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
